<template>
  <div class="signup">
    <v-row v-if="!signUpCompleted" align="center" justify="center" class="ma-0">
      <v-col
        class="text-center pa-0"
        cols="12"
        sm="10"
        md="8"
        lg="6"
        xl="4"
      >
        <div v-if="type === 'expert'" class="ps-2 pe-2">
          <v-stepper alt-labels class="mt-2">
            <v-stepper-header>
              <v-stepper-step step="1">
                {{ $t('globals.signUp') }}
              </v-stepper-step>

              <v-divider />

              <v-stepper-step step="2">
                {{ $t('expert.signup') }}
              </v-stepper-step>
            </v-stepper-header>
          </v-stepper>
          <h1 class="mt-0 mb-4 display-1">
            {{ $t('expert.signupTitle') }}
          </h1>
          <p>
            {{ $t('expert.signupDescShort') }}
          </p>
        </div>
        <div v-else class="ps-2 pe-2">
          <h1 class="mt-10 mb-4 display-1">
            {{ $t('globals.welcome') }}
          </h1>
          <h2
            v-if="referrerId && inviterUsername"
            v-html="
              $t('affiliate.signupWelcome', { USERNAME: inviterUsername })
            "
          />
        </div>

        <v-row align="center" justify="center" class="ma-2 ma-md-2">
          <v-col cols="12" md="6">
            <FacebookBtn flow="signup" />
          </v-col>
          <v-col cols="12" md="6">
            <GoogleBtn flow="signup" />
          </v-col>
        </v-row>
        <LineLabel :label="$t('signUp.or')" class="mt-10" />
        <v-card class="mx-auto pa-6 ma-0 mt-8" flat>
          <Alert
            v-if="error && !emailUniqueError && !usernameUniqueError"
            :dismissible="true"
            :message="$t('errorMessages.signUpFailed')"
            type="error"
          />

          <v-form ref="form" lazy-validation @submit.prevent="signup">
            <v-text-field
              v-model.lazy="form.email"
              :error-messages="emailErrors"
              :class="{ 'input--error': $v.form.email.$error }"
              :label="$t('globals.email')"
              :disabled="signUpLoading"
              :loading="emailLoading"
              required
              outlined
              @keydown="
                error = false
                $v.form.email.$reset()
              "
              @blur="$v.form.email.$touch()"
            />

            <v-text-field
              v-model.lazy="form.username"
              :error-messages="usernameErrors"
              :class="{ 'input--error': $v.form.username.$error }"
              :label="$t('globals.username')"
              :disabled="signUpLoading"
              :loading="usernameLoading"
              required
              outlined
              @keydown="
                error = false
                $v.form.username.$reset()
              "
              @blur="$v.form.username.$touch()"
            />

            <v-text-field
              v-model="form.password"
              :error-messages="passwordErrors"
              :class="{ 'input--error': $v.form.password.$error }"
              :label="$t('globals.password')"
              :disabled="signUpLoading"
              type="password"
              required
              outlined
              @keydown="
                error = false
                $v.form.password.$reset()
              "
              @blur="$v.form.password.$touch()"
            />

            <p class="body-2 mt-4">
              {{ $t('signUp.acceptance') }}&nbsp;
              <nuxt-link
                :to="localePath('terms-and-privacy-policy')"
                target="_blank"
                rel="noopener"
              >
                {{ $t('globals.terms') }}
              </nuxt-link>
            </p>

            <v-btn type="submit" class="mt-2" large color="primary">
              <i v-if="!signUpLoading" class="me-2 v-icon icon-account-plus" />
              <v-progress-circular
                v-if="signUpLoading"
                :size="24"
                :width="3"
                class="me-2"
                indeterminate
                color="white"
              />
              {{ $t('globals.signUp') }}
            </v-btn>
          </v-form>
        </v-card>
        <p class="mt-8">
          {{ $t('signUp.haveAnAccount') }}&nbsp;
          <nuxt-link
            :to="
              localePath({
                name: 'account-login',
                query: { redirect_uri: redirectUri, client_id: clientId },
              })
            "
          >
            {{ $t('globals.login') }}
          </nuxt-link>
        </p>
        <ul v-if="type === 'expert'" class="text-start mb-4 mt-8">
          <li v-for="(point, i) in expertSignupPoints" :key="i" class="ms-8">
            {{ point }}
          </li>
        </ul>
      </v-col>
    </v-row>

    <v-row v-if="signUpCompleted" align="center" justify="center" class="ma-0">
      <v-col
        class="pa-6 text-center"
        cols="12"
        sm="10"
        md="8"
        lg="6"
        xl="4"
      >
        <h1 class="mt-10 mb-4 display-1">
          {{ $t('globals.welcome') }}
        </h1>
        <Alert
          :title="$t('signUp.confirmationTitle')"
          :message="$t('signUp.confirmationDesc')"
          class="pa-6 text-center mt-8"
          type="success"
        />
        <v-btn class="ma-2" large color="primary" @click="login()">
          <i class="me-2 v-icon icon-login" />
          {{ $t('globals.continue') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { required, email, minLength, maxLength } from 'vuelidate/lib/validators'
import { scrollToFirstError } from '@/utils'
import { trackEvent } from '@/utils/tracking'
import Alert from '@/components/Alert'
import LineLabel from '@/components/LineLabel'
import FacebookBtn from '@/components/FacebookBtn'
import GoogleBtn from '@/components/GoogleBtn'

export default {
  auth: 'guest',

  components: {
    Alert,
    LineLabel,
    FacebookBtn,
    GoogleBtn
  },

  validations: {
    form: {
      email: {
        required,
        email,
        isUnique () {
          if (this.form.email === '') {
            return true
          }
          this.emailLoading = true
          return this.$axios
            .$post('/users/available', {
              field: 'email',
              value: this.form.email.trim()
            })
            .then((response) => {
              this.emailLoading = false
              this.emailUniqueError = !response.value
              return response.value
            })
        }
      },
      username: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(25),
        isUnique () {
          if (this.form.username === '') {
            return true
          }
          this.usernameLoading = true
          return this.$axios
            .$post('/users/available', {
              field: 'username',
              value: this.form.username.trim()
            })
            .then((response) => {
              this.usernameLoading = false
              this.usernameUniqueError = !response.value
              return response.value
            })
        }
      },
      password: {
        required,
        minLength: minLength(8)
      }
    }
  },

  async asyncData ({ route, $axios }) {
    const referrerId = route.query.referrer_id || ''
    const type = route.query.type || ''
    let inviterUsername = ''
    if (referrerId) {
      const inviterUser = await $axios
        .$get(`/users/${referrerId}`)
        .catch((e) => {
          return false
        })
      inviterUsername = inviterUser.username || ''
    }

    return {
      referrerId,
      inviterUsername,
      type
    }
  },

  data () {
    return {
      form: {
        email: '',
        username: '',
        password: ''
      },
      signUpCompleted: false,
      signUpLoading: false,
      emailLoading: false,
      usernameLoading: false,
      error: false,
      emailUniqueError: false,
      usernameUniqueError: false,
      redirectUri: '',
      clientId: '',
      isVasMarket: this.$t('markets').filter(
        i => i.country === this.$i18n.locale.substring(3, 5)
      )[0].paymentMethods.length
    }
  },

  head () {
    return {
      htmlAttrs: {
        dir: this.$t('meta.dir')
      },
      title: `${
        this.type === 'expert'
          ? this.$t('expert.signupPageTitle')
          : this.$t('globals.signUp')
      } — ${this.$t('globals.siteName')}`,
      meta: [
        {
          hid: 'og:title',
          property: 'og:title',
          content: `${this.$t('globals.siteName')} — ${
            this.type === 'expert'
              ? this.$t('expert.signupPageTitle')
              : this.$t('globals.signUp')
          }`
        },
        {
          hid: 'description',
          name: 'description',
          content: `${
            this.type === 'expert'
              ? this.$t('expert.signupTitle') +
                ' ' +
                this.$t('expert.signupDesc')
              : this.$t('signUp.description')
          }`
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: `${
            this.type === 'expert'
              ? this.$t('expert.signupTitle') +
                ' ' +
                this.$t('expert.signupDesc')
              : this.$t('signUp.description')
          }`
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: `${process.env.SITE_URL}/img/siteimage.jpg`
        },
        {
          hid: 'twitter:image',
          name: 'twitter:image',
          content: `${process.env.SITE_URL}/img/siteimage.jpg`
        }
      ]
    }
  },

  computed: {
    emailErrors () {
      const errors = []
      if (!this.$v.form.email.$dirty) {
        return errors
      }
      !this.$v.form.email.email &&
        errors.push(this.$t('errorMessages.emailInvalid'))
      !this.$v.form.email.required &&
        errors.push(this.$t('errorMessages.emailRequired'))
      !this.$v.form.email.isUnique &&
        errors.push(this.$t('errorMessages.emailAlreadyTaken'))
      return errors
    },
    usernameErrors () {
      const errors = []
      if (!this.$v.form.username.$dirty) {
        return errors
      }
      !this.$v.form.username.required &&
        errors.push(this.$t('errorMessages.usernameRequired'))
      !this.$v.form.username.minLength &&
        errors.push(this.$t('errorMessages.usernameIsTooShort'))
      !this.$v.form.username.maxLength &&
        errors.push(this.$t('errorMessages.usernameIsTooLong'))
      !this.$v.form.username.isUnique &&
        errors.push(this.$t('errorMessages.usernameAlreadyTaken'))
      return errors
    },
    passwordErrors () {
      const errors = []
      if (!this.$v.form.password.$dirty) {
        return errors
      }
      !this.$v.form.password.required &&
        errors.push(this.$t('errorMessages.passwordRequired'))
      !this.$v.form.password.minLength &&
        errors.push(this.$t('errorMessages.passwordLengthNotCorrect'))
      return errors
    },
    expertSignupPoints () {
      return this.$t('expert.signupPoints').filter(
        (point, i) => (i === 0 && this.isVasMarket) || i > 0
      )
    }
  },

  created () {
    this.redirectUri = this.$route.query.redirect_uri || ''
    this.clientId = this.$route.query.client_id || ''
    this.$store.commit('updateBreadcrumbs', [])
  },

  async mounted () {
    this.$auth.strategies.facebook.options.redirect_uri =
      location.protocol +
      '//' +
      location.host +
      '/consultations' +
      this.localePath({
        name: 'account-login',
        query: { provider: 'facebook' }
      })
    this.$auth.strategies.google.options.redirect_uri =
      location.protocol +
      '//' +
      location.host +
      '/consultations' +
      this.localePath({ name: 'account-login', query: { provider: 'google' } })
    sessionStorage.setItem('redirect_uri', this.redirectUri)
    sessionStorage.setItem('client_id', this.clientId)
    sessionStorage.setItem('referrer_id', this.referrerId)
    await this.$recaptchaLoaded()
  },

  methods: {
    async signup () {
      const currentCountry = this.$i18n.locale.substring(3, 5)
      const currentLanguage =
        this.$store.state.currentLang || this.$i18n.locale.substring(0, 2)
      await this.$v.form.$touch()
      this.$nextTick(() => {
        scrollToFirstError()
      })
      if (
        !this.$v.form.$error &&
        !this.emailUniqueError &&
        !this.usernameUniqueError
      ) {
        this.signUpLoading = true
        this.error = false
        this.emailUniqueError = false
        this.usernameUniqueError = false

        try {
          const recaptchaToken = await this.$recaptcha('signup')
          try {
            await this.$axios.$post('/users/register', {
              recaptcha_token: recaptchaToken,
              country: currentCountry,
              language: currentLanguage,
              email: this.form.email.trim(),
              username: this.form.username.trim(),
              password: this.form.password,
              referrer_id: parseInt(this.referrerId)
            })
            await this.$auth.loginWith('local', {
              data: {
                email: this.form.email,
                password: this.form.password
              }
            })
            trackEvent(
              'Sign up completed',
              { channel: 'organic' },
              this.$auth.user
            )
            if (this.type === 'expert') {
              this.$router.push(
                this.localePath({
                  name: 'experts-signup',
                  query: { step: '2' }
                })
              )
            } else {
              this.signUpCompleted = true
            }
          } catch (error) {
            this.signUpLoading = false
            this.error = true
          }
        } catch (recaptchaError) {
          this.signUpLoading = false
          this.error = true
        }
      } else {
        this.signUpLoading = false
        this.error = true
      }
    },

    login () {
      if (
        this.$route.query.redirect_uri &&
        this.$route.query.redirect_uri !== ''
      ) {
        if (
          this.$route.query.client_id &&
          this.$route.query.client_id === 'mahkamaty_fb_bot'
        ) {
          if (window && window.location) {
            window.location.href = `${this.$route.query.redirect_uri}&id=${this.$auth.user.id}`
          }
        } else {
          this.$router.push(this.$route.query.redirect_uri)
        }
      } else {
        this.$i18n.locale = `${this.$auth.user.language}-${this.$auth.user.country}`
        this.$router.push(this.localePath('index'))
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.v-card {
  border-radius: $standard-radius;
}
.v-stepper {
  background: unset !important;
  box-shadow: unset;
  .v-stepper__label {
    text-align: center !important;
  }
}
</style>
<style lang="scss">
.signup .v-stepper {
  background: unset !important;
  box-shadow: unset;
  .v-stepper__label {
    text-align: center !important;
    display: inline-block !important;
  }
}
</style>
